import { CPLAT_API_ROUTES, cplatApiV3, orderQueries, useFetch } from '@/api';
import SellerBoxAnimationData from '@/assets/animation/LoadingBox.json';
import { Dialog, DialogContent } from '@/components/atoms/Dialog';
import { InputBox } from '@/components/atoms/InputBox';
import { Button } from '@/components/atoms/NewPcButton';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import { useMembershipOrderPreviewTutorial } from '@/components/membership/MembershipOrderPreviewTutorial';
import { useToast } from '@/hooks/useToast';
import { useLog } from '@/hooks/V3/useLog';
import { stopLoading } from '@/redux/loadingSlice';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { deviceUuidState } from '@/store/atom';
import { CplatApiResponse } from '@/types/api';
import { Stores } from '@/types/stores';
import Lottie from 'lottie-react';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import { useOrderDetails } from '../../OrderDetailsProvider';
import { useQueryClient } from '@tanstack/react-query';

interface CancelControllerProps {
  setCoupangDialogOpen: (open: boolean) => void;
  setConfirmDialogOpen: (open: boolean) => void;
}

export const CancelController = ({
  setCoupangDialogOpen,
  setConfirmDialogOpen,
}: CancelControllerProps) => {
  const user = useAppSelector((state) => state.user);
  const isPreviewMode = useMembershipPreviewMode();
  const { openTutorial } = useMembershipOrderPreviewTutorial();

  const { checkedOrders } = useOrderDetails();

  const { data: storeListData } = useFetch<CplatApiResponse<Stores[][]>>(
    CPLAT_API_ROUTES.getStores,
    {
      method: 'POST',
      params: { cplatToken: user.CplatToken },
      config: { enabled: !isPreviewMode },
      v3: true,
    },
  );

  const hasCoupangOrderWithoutId = useMemo(
    () =>
      checkedOrders.some((order) => {
        if (order.marketInfo.marketNameEnglish !== 'coupang') return false;
        // 주문의 StoreId와 marketList의 StoreId가 일치하는 쿠팡 마켓 찾기
        const coupangMarket = storeListData?.data[0].find(
          (m) => m.marketAlias == 'coupang' && m.storeId === order.storeId,
        );
        return !coupangMarket?.coupangWingId || coupangMarket.coupangWingId === '';
      }),
    [checkedOrders, storeListData],
  );

  const handleCancelClick = () => {
    if (checkedOrders.length === 0) return;
    if (openTutorial('cancelOrder', checkedOrders.length)) return;

    if (hasCoupangOrderWithoutId) {
      setCoupangDialogOpen(true);
    } else {
      setConfirmDialogOpen(true);
    }
  };

  return (
    <Button
      size={'small'}
      variant={'line'}
      disabled={checkedOrders.length === 0}
      onClick={handleCancelClick}
    >
      주문취소
    </Button>
  );
};

interface CoupangIDModalProps {
  coupangDialogOpen: boolean;
  setCoupangDialogOpen: (open: boolean) => void;
  setConfirmDialogOpen: (open: boolean) => void;
  onSaveCoupangId: (coupangWingId: string) => void; // 아이디 저장 콜백
}

export const CoupangIDModal = ({
  coupangDialogOpen,
  setCoupangDialogOpen,
  setConfirmDialogOpen,
  onSaveCoupangId, // 추가: 아이디 저장 콜백
}: CoupangIDModalProps) => {
  const { watch, reset, setError, control } = useForm({
    defaultValues: { CoupangWingId: '' },
  });
  const CoupangWingId = watch('CoupangWingId');

  const handleSave = () => {
    if (!CoupangWingId) {
      setError('CoupangWingId', { message: '아이디를 입력하세요.' });
      return;
    }
    onSaveCoupangId(CoupangWingId); // 부모로 전달
    setCoupangDialogOpen(false);
    setConfirmDialogOpen(true);
    reset();
  };

  return (
    <Dialog open={coupangDialogOpen} onOpenChange={setCoupangDialogOpen}>
      <DialogContent noCloseBtn className="w-[380px]">
        <div className="flex flex-col justify-between">
          <div className="flex flex-col gap-1">
            <div className="text-gr-900 Title3S18">쿠팡 ID를 입력해 주세요</div>
            <ul className=" text-gr-700 Body5M14">
              <li>
                판매자 취소 기능 사용을 위해 ID 등록이 필요해요.
                <br />이 작업은 쿠팡 계정 별 최초 1번만 진행돼요.
              </li>
            </ul>
            <Controller
              name="CoupangWingId"
              control={control}
              render={({ field }) => (
                <InputBox
                  inputSize="medium"
                  {...field}
                  placeholder="아이디를 입력하세요"
                  className="mt-[20px] h-[52px]"
                />
              )}
            />
          </div>
          {/* 에러 메시지 출력 */}
          {/* {errors.CoupangWingId?.message && <p>{errors.CoupangWingId.message}</p>} */}
          <div className="mt-4 flex gap-2">
            <Button
              className="flex-1"
              variant="secondary"
              onClick={() => setCoupangDialogOpen(false)}
            >
              닫기
            </Button>
            <Button
              disabled={!CoupangWingId}
              className="flex-1"
              variant="solid"
              onClick={handleSave}
            >
              확인
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface CancelModalProps {
  confirmDialogOpen: boolean;
  setConfirmDialogOpen: (open: boolean) => void;
  coupangWingId: string; // 전달받은 쿠팡 아이디
}

export const CancelModal = ({
  confirmDialogOpen,
  setConfirmDialogOpen,
  coupangWingId, // 전달받은 쿠팡 아이디
}: CancelModalProps) => {
  const queryClient = useQueryClient();

  const { checkedOrders, refetchAllMarkets, setCheckedOrders } = useOrderDetails();
  const { toast } = useToast();
  const { openTutorial } = useMembershipOrderPreviewTutorial();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);
  const isLoading = useAppSelector((state) => state.loading.isLoading);

  const { data: storeListData } = useFetch<CplatApiResponse<Stores[][]>>(
    CPLAT_API_ROUTES.getStores,
    {
      method: 'POST',
      params: { cplatToken: user.CplatToken },
      v3: true,
    },
  );

  // 주문취소 처리 (cplatApiV2 사용)
  const handleOrderCancel = async () => {
    if (checkedOrders.length === 0) {
      toast({ icon: '⚠️', title: '취소할 주문을 선택해주세요', variant: 'dark' });
      return;
    }
    if (openTutorial('cancelOrder', checkedOrders.length)) return;

    // dispatch(startLoading());

    let successCount = 0;

    for (const order of checkedOrders) {
      const market = storeListData?.data[0].find(
        (m) =>
          m.marketNameEnglish === order.marketInfo.marketNameEnglish && m.storeId === order.storeId,
      );

      const params = {
        cplatToken: user.CplatToken,
        marketId: order.marketId,
        storeId: order.storeId,
        orderNo: order.orderNo,
        orderItemList: [order.orderItemNo],
        coupangShipmentBoxNo: order.coupangShipmentBoxNo,
        quantity: order.orderCount,
        coupangWingId: market?.coupangWingId || coupangWingId || '',
      };

      try {
        const res = await cplatApiV3.post('/order/cancel_by_seller', params);
        if (res.data?.code == '200') {
          successCount += 1;
          setUserEventHistory({
            eventName: 'cancel_by_seller',
            eventValue: order.orderNo,
            deviceUuid,
          });
        } else {
          console.warn(`주문 ${order.orderNo} 응답코드 실패`, res.data);
        }
      } catch (error) {
        console.error(`주문 ${order.orderNo} 취소 실패`, error);
      }
    }

    const isAllSuccess = successCount === checkedOrders.length;
    setConfirmDialogOpen(false);
    if (successCount > 0) {
      toast({
        icon: '✅',
        title:
          `${successCount}건 취소 처리 완료` +
          (!isAllSuccess ? ` (${checkedOrders.length - successCount}건 실패)` : ''),
        variant: 'dark',
      });
      refetchAllMarkets();
      setCheckedOrders([]);
    }

    if (successCount === 0) {
      toast({
        icon: '⚠️',
        title: `${checkedOrders.length}건 취소 실패`,
        variant: 'dark',
      });
    }

    dispatch(stopLoading());
    queryClient.invalidateQueries({ queryKey: orderQueries.all() });
    setCheckedOrders([]);
    setConfirmDialogOpen(false);
  };

  return (
    <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
      <DialogContent noCloseBtn className="w-[380px] p-[30px]">
        {isLoading && (
          <div className="pointer-events-auto absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center rounded-[20px] bg-black bg-opacity-[0.05]">
            <Lottie className="w-[150px]" animationData={SellerBoxAnimationData} loop />
          </div>
        )}
        <div className="flex flex-col justify-between">
          <div className="flex flex-col gap-1">
            <div className="text-gr-900 Title3S18">판매자 주문 취소를 진행할게요</div>
            <ul className="list-disc pl-6 text-gr-700 Body5M14">
              <li>
                결제 완료 상품의 주문을 취소할 경우 마켓 정책에 따라 상품 품절로 인한 주문 거절로
                사유가 등록돼요.
                <br />
                *각 마켓 정책에 따라 상품이 품절 상태로 변경될 수 있습니다.
              </li>
              <li>
                기타 사유로 주문 취소가 필요할 경우 마켓 판매자 페이지에서 직접 처리를 진행해
                주세요.
              </li>
            </ul>
          </div>

          <div className="mt-5 flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setConfirmDialogOpen(false)}
            >
              닫기
            </Button>
            <Button variant="solid" className="h-[44px] flex-1" onClick={handleOrderCancel}>
              확인
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
